import React from 'react';
import { X, Mountain, Waves, Trees, Castle, Square } from 'lucide-react';
import { PrebuiltScene } from '../constants';

interface SceneSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  scenes: PrebuiltScene[];
  onSelectScene: (scene: PrebuiltScene) => void;
}

const getSceneIcon = (sceneId: string) => {
  switch (sceneId) {
    case 'volcano':
      return <Mountain className="w-8 h-8 text-orange-500" />;
    case 'waterfall':
      return <Waves className="w-8 h-8 text-blue-500" />;
    case 'forest':
      return <Trees className="w-8 h-8 text-green-500" />;
    case 'castle':
      return <Castle className="w-8 h-8 text-gray-400" />;
    case 'blank':
      return <Square className="w-8 h-8 text-gray-500" />;
    default:
      return <Square className="w-8 h-8 text-gray-500" />;
  }
};

const getSceneColor = (sceneId: string): string => {
  switch (sceneId) {
    case 'volcano':
      return 'from-orange-900/50 to-red-900/50 border-orange-500/30';
    case 'waterfall':
      return 'from-blue-900/50 to-cyan-900/50 border-blue-500/30';
    case 'forest':
      return 'from-green-900/50 to-emerald-900/50 border-green-500/30';
    case 'castle':
      return 'from-gray-800/50 to-slate-800/50 border-gray-500/30';
    case 'blank':
      return 'from-gray-900/50 to-gray-800/50 border-gray-600/30';
    default:
      return 'from-gray-900/50 to-gray-800/50 border-gray-600/30';
  }
};

const SceneSelector: React.FC<SceneSelectorProps> = ({
  isOpen,
  onClose,
  scenes,
  onSelectScene,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Choose a Scene</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-gray-400 mb-4">
            Select a pre-built scene to start with, or choose Blank Canvas to create your own.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenes.map((scene) => (
              <button
                key={scene.id}
                onClick={() => onSelectScene(scene)}
                className={`group relative p-4 rounded-xl border-2 transition-all duration-200 hover:scale-[1.02] bg-gradient-to-br ${getSceneColor(scene.id)}`}
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-gray-900/50 rounded-xl group-hover:bg-gray-900/70 transition-colors">
                    {getSceneIcon(scene.id)}
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg">{scene.name}</h3>
                    <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                      {scene.description}
                    </p>
                  </div>
                </div>

                {/* Hover effect */}
                <div className="absolute inset-0 rounded-xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SceneSelector;
